import { mergeClasses } from '@fluentui/react-components';
import AppContext from 'App/AppContext';
import { globalAppName } from 'globalConstants';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import logger from 'services/Logging/logService';
import { LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import NavMainV9, { NavPresentation } from './NavMainV9';
import { menuCollapsedWidth, menuExpandedWidth, useNavHostStyles } from './navMainStylesV9';
import NavRootOverlay from './NavRootOverlay';

//
// The desktop host of the main menu: the expanded rail, or the compact icon rail whose hover opens the full
// menu over the page (NavRootOverlay). The hamburger in the top bar toggles expanded <-> compact.
//
const NavRoot = () => {
  const appContext = useContext(AppContext);
  const location = useLocation();
  const styles = useNavHostStyles();
  const [expandedOnHover, setExpandedOnHover] = useState<boolean>(false);

  useEffect(() => {
    //log all location changes
    let strLocation = location.pathname;
    if (location.hash) {
      strLocation += location.hash;
    }
    if (location.search) {
      strLocation += ' ' + location.search;
    }
    logger.debug('Location: ' + strLocation);
    //set browser tab name
    if (appContext.isAuthenticated && appContext.user.tenant.name) {
      document.title = `${appContext.user.tenant.name} - ${globalAppName}`;
    } else {
      document.title = globalAppName;
    }
  }, [location, appContext.isAuthenticated, appContext.user.tenant.name]);

  useEffect(() => {
    setLocalStorageData(appContext, LocalStorageKeys.MainNavCollapsed, appContext.isMainNavCollapsed.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appContext.isMainNavCollapsed]);

  // Picking a page ends the peek: the flyout closes when the location changes.
  useEffect(() => {
    setExpandedOnHover(false);
  }, [location.pathname]);

  const collapsed = appContext.isMainNavCollapsed;
  const presentation: NavPresentation = collapsed ? 'icons' : 'full';
  const width = collapsed ? menuCollapsedWidth : menuExpandedWidth;

  //
  // Main render
  //
  // The rail stays mounted while the flyout is open, so what the user expanded survives hovering.
  //
  return (
    <div
      className={mergeClasses(styles.root)}
      style={{ width: width, minWidth: width }}
      onMouseEnter={() => {
        if (collapsed) {
          setExpandedOnHover(true);
        }
      }}
    >
      <NavMainV9 presentation={presentation} />
      {collapsed && expandedOnHover && <NavRootOverlay onClose={() => setExpandedOnHover(false)} />}
    </div>
  );
};

export default NavRoot;
